export const administrationPage = {
  supervisingIdsPanel: {
    providerAdminChip: 'Provider Admin',
    supervisingIdLabel: 'Supervising ID'
  },
  selectCompaniesForm: {
    columns: {
      name: 'Name',
      createdAt: 'Created At'
    }
  },
  assignUsersStep: {
    newUserTab: 'New User'
  },
  clientOrgForm: {
    removeLocationTooltip: 'Remove Location'
  },
  allCompanies: {
    columns: {
      name: 'Name',
      createdOn: 'Created On',
      goLiveDate: 'Go Live Date'
    },
    loadErrorMessage: 'Could not load pharmacy details'
  },
  systemConfigurations: {
    systemOptionsTab: 'SYSTEM OPTIONS',
    rolloutGroupsTab: 'ROLLOUT GROUPS',
    medConsultTemplatesTab: 'MED CONSULT TEMPLATES',
    featureFlagOverridesTab: 'FEATURE FLAG OVERRIDES'
  },
  featureFlagOverridesTab: {
    heading: 'Feature Flag Overrides',
    description:
      "Turns a feature on or off for all pharmacies at once, by writing it into every pharmacy's settings. To change a single pharmacy, use its pharmacy settings (Pharmacy → Advanced → System Configurations).",
    scopeNote: 'Only the AdditionalFeatures flags are listed here. A pharmacy without a flag counts as its default.',
    changeNote: 'Click On or Off to change a flag for every pharmacy. You will be asked to confirm, then it saves straight away.',
    newPharmaciesNote: 'New pharmacies: what new RPM pharmacies start with. Every change made here updates it too.',
    columnFlag: 'Flag (AdditionalFeatures)',
    columnCurrent: 'Pharmacies now',
    columnChange: 'Set for all pharmacies',
    columnNewPharmacies: 'New pharmacies',
    allOn: (count: number) => `On for all ${count}`,
    allOff: (count: number) => `Off for all ${count}`,
    mixed: (on: number, off: number) => `Mixed: ${on} on, ${off} off`,
    on: 'On',
    off: 'Off',
    confirmTitle: (key: string, on: boolean) => `Turn ${key} ${on ? 'on' : 'off'} for every pharmacy?`,
    confirmMessage: (key: string, on: boolean) =>
      `This sets <b>${key}: ${
        on ? 'On' : 'Off'
      }</b> in every pharmacy's settings and for new pharmacies. Users see it after they sign in again.`,
    confirmAgree: 'Yes',
    confirmDisagree: 'No',
    updateSuccessMessage: (count: number) => `Updated ${count} pharmacies`,
    updateErrorMessage: 'Could not update the pharmacies',
    loadErrorMessage: 'Could not load the feature flags'
  },
  rolloutGroupsTab: {
    heading: 'Rollout Groups',
    description:
      'Which build each pharmacy is served. Moving a pharmacy takes effect the next time its users reload — they are prompted, not reloaded automatically.',
    searchPlaceholder: 'Search by pharmacy name',
    groupFilterLabel: 'Rollout group',
    allGroupsOption: 'All',
    columnName: 'Pharmacy',
    columnGroup: 'Rollout group',
    columnActive: 'Active',
    activeYes: 'Yes',
    activeNo: 'No',
    countSummary: (shown: number, total: number) => `Showing ${shown} of ${total} pharmacies`,
    confirmTitle: 'Move this pharmacy?',
    confirmMessage: (name: string, to: string) =>
      `Move <b>${name}</b> to the <b>${to}</b> build?<br /><br />Its users are not signed out and nothing reloads on its own — each is asked to reload the next time the portal checks, and can keep working until they choose to.`,
    confirmAgree: 'Move',
    confirmDisagree: 'Cancel',
    updateSuccessMessage: (name: string, to: string) => `${name} moved to ${to}`,
    updateErrorMessage: 'Could not change the rollout group',
    loadErrorMessage: 'Could not load rollout groups'
  },
  dataImport: {
    importRunsTab: 'Import Runs',
    newImportTab: 'New Import',
    historyTab: 'History',
    rakeTasksTab: 'Rake Tasks'
  },
  rakeTasks: {
    runTitle: 'Run a rake task',
    runDescription: 'Paste a command, or pick a task from the list to fill in its arguments.',
    commandPlaceholder: 'insurance:accounts_insurance_plans_parser RAILS_LOG_TO_STDOUT=1',
    commandAriaLabel: 'Rake command',
    commandHint: 'Paste it the way you run it locally. bundle exec rake and quotes are removed for you.',
    rewrittenHint: 'Will run as',
    editAsTextLabel: 'Edit as text',
    clearLabel: 'Clear',
    runButton: 'Run task',
    runFootnote: 'Runs in its own container against this environment. One task runs at a time.',
    optionalArgumentsHint: 'Arguments are optional. Leave a box empty to skip it.',
    noArgumentsHint: 'This task takes no arguments.',
    envLabel: 'Environment variables',
    envHelper: 'Optional. Space separated, for example RAILS_LOG_TO_STDOUT=1 FORMAT=csv',
    confirmTitle: 'Add this task to the stack?',
    confirmMessage: 'It runs against the database of the environment you are signed in to. You can stop it from the stack once it starts.',
    confirmAgree: 'Add to stack',
    confirmDisagree: 'Cancel',
    runFailedMessage: 'Could not start the task',
    searchPlaceholder: 'Search tasks',
    searchPlaceholderWithCount: (total: number) => `Search ${total} tasks`,
    taskCount: (shown: number, total: number) => `${shown} of ${total} tasks`,
    topLevelGroup: 'No namespace',
    loadTasksErrorTitle: 'The task list could not be loaded',
    loadTasksErrorHint: 'You can still type a command into the panel above.',
    loadTasksRetry: 'Try again',
    noMatchesMessage: (search: string) => `No tasks match "${search}".`,
    noTasksMessage: 'No tasks available.',
    addToStackButton: 'Add to stack',
    queueFootnote: (waiting: number) =>
      waiting === 1
        ? 'One task is already in the stack. This one runs after it.'
        : `${waiting} tasks are already in the stack. This one runs after them.`,
    someone: 'someone',
    list: {
      addButton: 'Add to list',
      title: (count: number) => (count === 1 ? '1 task in the list' : `${count} tasks in the list, run in this order`),
      runButton: (count: number) => `Run ${count} ${count === 1 ? 'task' : 'tasks'}`,
      footnote:
        'Each task is added to the run stack and starts in its own container when the one before it finishes. Environment settings apply to all of them.',
      moveUp: 'Move up',
      moveDown: 'Move down',
      remove: 'Remove from list',
      confirmTitle: (count: number) => `Run these ${count} tasks in order?`,
      confirmMessage: 'Each one is added to the run stack and starts after the one before it finishes, even if that one fails.',
      confirmAgree: 'Run in order'
    },
    statuses: {
      PENDING: 'Waiting',
      STARTING: 'Starting',
      RUNNING: 'Running',
      STOPPING: 'Stopping',
      SUCCEEDED: 'Succeeded',
      FAILED: 'Failed',
      STOPPED: 'Stopped',
      CANCELLED: 'Cancelled',
      LOST: 'Lost'
    },
    stack: {
      title: 'Run stack',
      idle: 'Nothing running',
      count: (total: number) => (total === 1 ? '1 task' : `${total} tasks`),
      empty: 'Nothing is running. Tasks you add run one at a time, in the order you add them.',
      someone: 'someone',
      waitingMeta: (by: string) => `Added by ${by}, waiting its turn`,
      activeMeta: (by: string, elapsed: string) => `Started by ${by}, ${elapsed} ago`,
      stop: 'Stop',
      remove: 'Remove',
      keepGoing: 'Leave it',
      stopTitle: 'Stop this task?',
      stopMessage:
        'The task is asked to stop and gives up within a few seconds. Whatever it has already changed stays changed \u2014 nothing is undone. The next task in the stack starts after it.',
      stopConfirm: 'Stop it',
      removeTitle: 'Remove this task from the stack?',
      removeMessage: 'It has not started, so nothing has changed. You can add it again whenever you like.',
      removeConfirm: 'Remove it',
      forceRemove: 'Force remove',
      forceTitle: 'Force remove this task from the stack?',
      forceMessage:
        'It was asked to stop but has not let go. This takes it off the stack now so the next task can start. If its container is somehow still running, stop it in AWS.',
      forceConfirm: 'Force remove',
      stopFailedMessage: 'Could not stop the task'
    },
    timeline: {
      queued: 'Requested',
      started: 'Started',
      finished: 'Finished',
      failed: 'Failed',
      lost: 'Last heard from',
      stopped: 'Stopped',
      cancelled: 'Removed',
      waited: (duration: string) => `waited ${duration}`,
      ran: (duration: string) => `ran ${duration}`
    },
    history: {
      title: 'Recent runs',
      description: 'Every task started from this screen, newest first. Open one to see its output.',
      retention: 'Only the last 100 runs are kept.',
      empty: 'No runs yet. Tasks you run appear here with their output.',
      columns: {
        status: 'Status',
        command: 'Command',
        startedBy: 'Started by',
        started: 'Started',
        duration: 'Duration'
      }
    },
    detail: {
      back: 'Rake tasks',
      meta: (by: string, at: string, runner: string) => `Started by ${by} on ${at}, runner ${runner}`,
      startingHint: 'Starting a container for this task. This usually takes one to three minutes.',
      pendingHint: 'Waiting for the tasks ahead of it in the stack to finish.',
      stoppingHint: 'Asked to stop. The task gives up at its next check, usually within a few seconds.',
      failedTitle: 'The task stopped with an error',
      lostTitle: 'This run stopped reporting',
      lostAfterStart:
        'The container stopped checking in while the task was running. It may have run out of memory or been stopped. The output below is everything it saved.',
      lostBeforeStart: 'The container never started the task. Check the runner in AWS, then run the task again.'
    },
    log: {
      title: 'Output',
      lineCount: (count: number) => `${count} lines`,
      matchCount: (shown: number, total: number) => `${shown} of ${total} lines`,
      filterPlaceholder: 'Filter output',
      follow: 'Follow',
      copy: 'Copy output',
      copiedMessage: 'Output copied',
      copyFailedMessage: 'Could not copy the output',
      waiting: 'Waiting for the task to print something…',
      empty: 'This run printed nothing.',
      noMatches: 'No lines match the filter.'
    },
    errors: {
      empty: 'Enter a rake task.',
      noTask: 'No task name found.',
      invalidCharacters: 'Only letters, numbers and . - _ / : @ + % , are allowed.',
      invalidArgument: 'Arguments cannot contain commas or spaces.'
    }
  },
  medConsultTemplatesConfigTab: {
    showArchivedLabel: 'Show archived',
    archivedChip: 'Archived'
  },
  orgHandleConfigForm: {
    updateSuccessMessage: 'Organization updated successfully',
    updateFailedMessage: 'Organization update failed',
    orgHandleNameLabel: 'Org Handle Name'
  },
  runDetail: {
    columns: {
      row: 'Row',
      outcome: 'Outcome',
      detail: 'Detail',
      truentityId: 'Truentity ID',
      message: 'Message'
    }
  },
  createMedConsultTemplateDialog: {
    nameRequiredMessage: 'Template name is required',
    createdMessage: 'Template created',
    createFailedMessage: 'Failed to create template',
    title: 'New Intake (HPI) Template',
    nameLabel: 'Name',
    categoryLabel: 'Category'
  },
  importHistory: {
    columns: {
      created: 'Created',
      type: 'Type',
      pharmacy: 'Pharmacy',
      file: 'File',
      status: 'Status',
      onboardedSkippedFailed: 'Onboarded / Skipped / Failed',
      uploadedBy: 'Uploaded by'
    }
  },
  systemOptionsTab: {
    fixJsonErrorsMessage: 'Fix JSON errors before switching to Tree view.',
    invalidJsonMessage: 'Cannot save — JSON is invalid.',
    updateSuccessMessage: 'System option updated successfully',
    updateFailedMessage: 'Failed to update system option',
    editorModeAriaLabel: 'Editor mode',
    treeEditorAriaLabel: 'Tree editor',
    rawJsonEditorAriaLabel: 'Raw JSON editor',
    searchPlaceholder: 'Search option keys',
    noMatchesMessage: 'No option keys match your search.'
  },
  pioneerRxDetailsForm: {
    credentialsTitle: 'Setup PioneerRx Authentication Credentials',
    usernameRequiredMessage: 'Please enter a username to continue.',
    passwordRequiredMessage: 'Please enter a password to continue.',
    clientLocationRequiredMessage: "We couldn't find the specified client location.",
    saveFailedMessage: 'Cannot save pioneerRx settings. Please contact support',
    savedMessage: 'PioneerRx settings saved successfully',
    organizationLabel: 'Organization',
    selectOptionPlaceholder: 'Select an option...',
    clientLocationLabel: 'Client Location'
  },
  userSettingTab: {
    otpRequiredMessage: 'At least one OTP method must be enabled.',
    editSettingsAs: 'Edit Settings as:',
    formTab: 'Form',
    signInVerificationSection: 'Sign-in Verification',
    emailOtpLabel: 'Email OTP',
    smsOtpLabel: 'SMS OTP',
    sendCodeTo: (destination: string) => `Send a code to ${destination}`,
    emailOtpUnavailable: 'Add an email on the Profile tab to enable',
    smsOtpUnavailable: 'Add a mobile phone on the Profile tab to enable',
    generalSection: 'General',
    titleLabel: 'Title',
    descriptionLabel: 'Description',
    rpmReportSection: 'RPM Report',
    rpmReportCaption: 'Contact details printed on RPM reports.',
    phoneNumberLabel: 'Phone Number',
    faxNumberLabel: 'Fax Number',
    phonePlaceholder: '(000) 000-0000',
    textTab: 'Text',
    jsonTab: 'JSON'
  },
  assignUserForm: {
    columns: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      role: 'Role',
      title: 'Title'
    },
    firstNameLabel: 'First Name',
    lastNameLabel: 'Last Name',
    searchLabel: 'Search',
    resetLabel: 'Reset'
  },
  addUserForm: {
    otpRequiredMessage: 'At least one OTP method must be enabled.',
    pharmacyOwnerLabel: 'Pharmacy Owner',
    observerLabel: 'Observer',
    rpmPrimaryLabel: 'RPM Primary',
    firstNameLabel: 'First Name',
    lastNameLabel: 'Last Name',
    selectOptionPlaceholder: 'Select an option...',
    titleLabel: 'Title',
    providerTitleLabel: 'Provider Title',
    emailLabel: 'Email',
    emailOtpLabel: 'Email OTP',
    mobilePhoneLabel: 'Mobile Phone',
    smsOtpLabel: 'SMS OTP',
    npiNumberLabel: 'NPI Number',
    licenseNumberLabel: 'License Number',
    supervisingIdLabel: 'Supervising ID'
  },
  addCompanyForm: {
    nameLabel: 'Name',
    nameTagLabel: 'Name Tag',
    removeLocationTooltip: 'Remove location',
    companyNameLabel: 'Pharmacy Name',
    rpmChipLabel: 'RPM',
    rpmChipDescription: 'Remote Patient Monitoring',
    ccmChipLabel: 'CCM',
    ccmChipDescription: 'Chronic Care Management',
    mtmChipLabel: 'MTM',
    mtmChipDescription: 'Medication Therapy Management',
    tcmChipLabel: 'TCM',
    tcmChipDescription: 'Transitional Care Management',
    servicesDefaultCaption:
      "ⓘ RPM and CCM is enabled by default. Uncheck if this pharmacy won't use Remote Patient Monitoring or Chronic Care Management.",
    mockCompanyLabel: 'Mock pharmacy',
    organizationNameLabel: 'Organization Name',
    locationNameRequiredMessage: 'Location name is required',
    nameTagRequiredMessage: 'Name tag is required',
    companyNameTakenMessage: 'Pharmacy name already taken',
    companyNameRequiredMessage: 'Pharmacy Name is required',
    nameAvailableMessage: 'Name is available',
    orgNameHelperText: 'Automatically matches the pharmacy name. You can override it.',
    addLocationLabel: 'Add Location'
  },
  importWizard: {
    fileAnalyzedMessage: 'File uploaded and analyzed',
    mappingSavedMessage: 'Mapping saved',
    importStartedMessage: 'Import started',
    uploadStepLabel: 'Upload',
    importTypeLabel: 'Import type',
    pharmacyLabel: 'Pharmacy',
    clientOrganizationLabel: 'Client organization',
    clientStoreLabel: 'Client store',
    mapColumnsStepLabel: 'Map columns',
    mappedChip: 'Mapped',
    notMappedChip: 'Not mapped',
    previewTestStepLabel: 'Preview & test',
    columns: {
      row: 'Row',
      outcome: 'Outcome',
      detail: 'Detail',
      message: 'Message'
    },
    confirmStepLabel: 'Confirm'
  },
  addressConfigSection: {
    savedMessage: 'Address saved successfully',
    saveFailedMessage: 'Could not save address',
    addressLine1Label: 'Address Line 1',
    addressLine2Label: 'Address Line 2',
    cityLabel: 'City',
    stateLabel: 'State',
    zipcodeLabel: 'Zip Code',
    requiredMessage: 'Required',
    invalidZipMessage: 'Enter a 5 digit zip code',
    incompleteMessage: 'Address Line 1, City, State, and a valid Zip Code are required.',
    resetLabel: 'Reset',
    saveLabel: 'Save'
  },
  athenaConfigSection: {
    savedMessage: 'Athena configuration saved successfully',
    saveFailedMessage: 'Could not save Athena configuration',
    departmentIdLabel: 'Athena Department Id',
    resetLabel: 'Reset',
    saveLabel: 'Save'
  },
  dailyRpmReportEmailsSection: {
    savedMessage: 'Email targets saved successfully',
    saveFailedMessage: 'Could not save email targets',
    emailAddressLabel: 'Email Address',
    bccEmailsLabel: 'BCC Emails',
    bccEmailsPlaceholder: 'Type email and press Enter, or paste a list',
    activeLabel: 'Active',
    resetLabel: 'Reset',
    saveLabel: 'Save',
    unsavedChangesTitle: 'Unsaved Changes',
    unsavedChangesMessage:
      'You have unsaved changes to the daily RPM report email recipients. If you leave now, these changes will be lost. Are you sure you want to leave?'
  },
  organizationConfigSection: {
    savedMessage: 'Organization handle saved successfully',
    saveFailedMessage: 'Could not save organization handle',
    orgHandleNameLabel: 'Org Handle Name',
    resetLabel: 'Reset',
    saveLabel: 'Save'
  },
  pioneerRxConfigSection: {
    credentialsTitle: 'Setup PioneerRx Authentication Credentials',
    usernameRequiredMessage: 'Please enter a username to continue.',
    passwordRequiredMessage: 'Please enter a password to continue.',
    clientLocationRequiredMessage: "We couldn't find the specified client location.",
    saveFailedMessage: 'Cannot save PioneerRx settings. Please contact support',
    savedMessage: 'PioneerRx settings saved successfully',
    organizationLabel: 'Organization',
    selectOptionPlaceholder: 'Select an option...',
    clientLocationLabel: 'Client Location',
    webhookUrlLabel: 'PioneerRx Webhook URL'
  },
  primaryContactConfigSection: {
    savedMessage: 'Contact details saved successfully',
    saveFailedMessage: 'Could not save contact details',
    emailAddressLabel: 'Email Address',
    phoneNumberLabel: 'Phone Number',
    resetLabel: 'Reset',
    saveLabel: 'Save'
  },
  rpmProviderConfigSection: {
    unassignErrorMessage: 'Error while unassigning provider',
    unassignTitle: 'Unassign Provider',
    updateTitle: 'Update Provider',
    columns: {
      firstName: 'First Name',
      lastName: 'Last Name',
      npiNumber: 'NPI Number',
      email: 'Email',
      rpmReviewer: 'RPM Reviewer',
      primary: 'Primary',
      phone: 'Phone',
      action: 'Action'
    },
    assignProvidersLabel: 'ASSIGN PROVIDERS'
  },
  tenoviConfigSection: {
    savedMessage: 'Tenovi configuration saved successfully',
    saveFailedMessage: 'Could not save Tenovi configuration',
    clientNameLabel: 'Tenovi Client Name',
    apiKeyLabel: 'Tenovi API Key',
    secondaryApiKeyLabel: 'Tenovi Secondary API Key',
    resetLabel: 'Reset',
    saveLabel: 'Save'
  },
  configurationStep: {
    addressTab: 'Address',
    primaryContactTab: 'Primary Contact',
    tenoviTab: 'Tenovi',
    athenaTab: 'Athena',
    rpmPrimaryProviderTab: 'RPM Primary Provider',
    pioneerRxTab: 'PioneerRx',
    organizationTab: 'Organization',
    dailyRpmReportEmailsTab: 'Daily RPM Report Emails'
  },
  medConsultTemplateItemCard: {
    questionRequiredMessage: 'Question text is required',
    savedMessage: 'Question saved',
    deletedMessage: 'Question deleted',
    deleteTitle: 'Delete question',
    mandatoryLabel: 'Mandatory',
    questionLabel: 'Question',
    subLabelContextLabel: 'Sub-label / context',
    clinicianGuidanceLabel: 'Clinician guidance',
    conditionTagLabel: 'Condition tag',
    orderLabel: 'Order',
    removeOptionAriaLabel: 'Remove option',
    conditionalChip: 'Conditional',
    conditionsHeading: 'Show only when… (leave empty to always show)',
    conditionsHelp:
      'The question is enabled — and required, if mandatory — when any condition matches. Otherwise it is skipped and recorded as "Not applicable".',
    controllingQuestionLabel: 'When the answer to',
    operatorLabel: 'Is',
    operatorIn: 'one of',
    operatorNotIn: 'not one of',
    valuesLabel: 'Values',
    addConditionButton: 'Add condition',
    removeConditionAriaLabel: 'Remove condition',
    saveQuestionsFirstMessage: 'Save the other questions first to reference them in a condition.',
    incompleteConditionMessage: 'Each condition needs a question and at least one value'
  },
  notificationFiles: {
    adtNotificationFileReceivedOption: 'ADT Notification File Received',
    adtNotificationFileImportedOption: 'ADT Notification File Imported',
    adtPatientPanelFileImportedOption: 'ADT Patient Panel File Imported',
    adtPatientPanelFileSentOption: 'ADT Patient Panel File Sent',
    allOption: 'All',
    columns: {
      fileName: 'File Name',
      status: 'Status',
      activityOn: 'Activity On',
      logText: 'Log Text',
      endpoint: 'Endpoint'
    },
    statusLabel: 'Status',
    selectOptionPlaceholder: 'Select an option...'
  },
  scheduledReportsList: {
    columns: {
      name: 'Name',
      organization: 'Organization',
      status: 'Status',
      reportType: 'Report Type',
      createdAt: 'Created At',
      downloadCsv: 'Download CSV'
    },
    completedChip: 'Completed',
    failedChip: 'Failed',
    processingChip: 'Processing',
    scheduledChip: 'Scheduled',
    queuedChip: 'Queued',
    runningChip: 'Running',
    fetchErrorMessage: 'Failed to retrieve reports'
  },
  medConsultTemplateEditor: {
    nameRequiredMessage: 'Template name is required',
    updatedMessage: 'Template updated',
    archiveTitle: 'Archive template',
    archiveMessage: 'Archiving hides this template from clinicians. Existing intakes are unaffected. Continue?',
    archivedChip: 'Archived',
    nameLabel: 'Name',
    categoryLabel: 'Category'
  },
  insurancePlansGrid: {
    columns: {
      payer: 'Payer',
      plan: 'Plan',
      sheetStatus: (sheetLabel: string) => `${sheetLabel} status`,
      insuranceType: 'Insurance Type',
      category: 'Category',
      athenaPlanType: 'Athena Plan Type',
      bin: 'BIN',
      pcn: 'PCN',
      rxGroup: 'RxGroup',
      states: 'States',
      athenaPackage: 'Athena Pkg',
      relyingPartyPatientCount: (sourceLabel: string) => `My Patients (Pharmacy · ${sourceLabel})`,
      patientCount: (sourceLabel: string) => `All Patients (Global · ${sourceLabel})`
    }
  },
  athenaPackages: {
    columns: {
      package: 'Package',
      payer: 'Payer',
      insuranceType: 'Insurance Type',
      planType: 'Plan Type',
      states: 'States',
      myPatients: 'My Patients',
      allPatients: 'All Patients'
    },
    nationwide: 'Nationwide',
    stateLabel: 'State',
    insuranceTypeLabel: 'Insurance type',
    searchPlaceholder: 'Search package, name or payer',
    missingTitle: (count: number) => `${count} package${count === 1 ? ' is' : 's are'} used by patients but missing from the list`,
    missingDescription: (patients: number) =>
      `${patients.toLocaleString()} patients are on them. Add them to the Athena package sheet, then resync.`,
    showMissing: 'Show them',
    showAll: 'Show all packages'
  },
  insurancePlansSyncResult: {
    athenaTitle: 'Athena package list',
    athenaNotSaved: 'The package list was not saved. Try the resync again.',
    athenaSummary: (total: number, added: number, changed: number) =>
      [`${total.toLocaleString()} packages`, added > 0 ? `${added} added` : null, changed > 0 ? `${changed} changed` : null]
        .filter(Boolean)
        .join(' · '),
    athenaStates: (states: string[]) => `States: ${states.join(', ')}`,
    athenaWrongState: (count: number) =>
      `${count} sheet row${count === 1 ? ' names' : 's name'} a state its tab doesn't cover. Check the Athena package sheet.`,
    athenaOnlyNote: 'Only the Athena package list was refreshed. The Add-Insurance wizard now offers these packages.',
    carelinkNote: "This refreshed the plan table only. Patients stay on the plan they're already matched to.",
    conflictsNote:
      'A conflict means the payer and plan already exist in another state, so their BIN, PCN and RxGroup were left as they were.'
  }
} as const;
